import { inject, Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { forkJoin, map, take } from 'rxjs';
import { appStatusMap, Status } from '../constants/AppStatuses';
import { UserRoles } from '../models/User';
import { StatusService } from '../services/status.service';
import { UserService } from '../services/user.service';

@Injectable({ providedIn: 'root' })
export class UserAccessGuard implements CanActivate {
  private readonly userService: UserService = inject(UserService);
  private readonly router: Router = inject(Router);
  private readonly statusService: StatusService = inject(StatusService);

  canActivate() {
    return forkJoin([this.userService.getUnit(), this.userService.getRoles()]).pipe(
      take(1),
      map(([unit, userRoles]) => {
        if (this.isOnlyAdmin(userRoles)) {
          this.router.navigate(['/configuration'], {
            queryParamsHandling: 'merge'
          });

          return false;
        }

        if (this.hasAppAccess(userRoles)) {
          return true;
        }

        // No access to Unit content
        this.statusService.setStatus({
          ...appStatusMap[Status.ACCESS_DENIED],
          params: { unitName: unit?.name ? `<b>${unit.name}</b>` : '' }
        });

        this.router.navigate(['/unavailable'], {
          queryParamsHandling: 'merge'
        });
        return false;
      })
    );
  }

  private isOnlyAdmin(userRoles: UserRoles): boolean {
    return userRoles.isAdmin && !userRoles.isNurse && !userRoles.isPhysician;
  }

  private hasAppAccess(userRoles: UserRoles): boolean {
    return userRoles.isNurse || userRoles.isPhysician;
  }
}
